import { NewAppsDiscoveryTrendGrain } from "@graphql/generated/graphql.g";
import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  DashboardChartFrame,
  type DashboardDateRange,
} from "@components/Dashboards/shell";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { LineChart } from "@design-system/LineChart/LineChart";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { useNewAppsDiscoveryTrendQuery } from "./NewAppsDiscoveryTrendChart.g";
import classes from "./NewAppsDiscoveryTrendChart.module.css";

type TenantRow = {
  clientId: string;
  clientName: string;
  count: number;
};

type Props = {
  /** When set, scopes the series to one tenant and hides the MSP breakdown. */
  clientId?: string;
  dateRange: DashboardDateRange;
  onSummaryChange?: (summary: {
    totalCount: number;
    isLoading: boolean;
  }) => void;
};

const columnHelper = createColumnHelper<TenantRow>();
const PAGE_SIZE = 8;

const resolveGrain = (
  start: Date,
  end: Date,
): NewAppsDiscoveryTrendGrain => {
  const days = dayjs(end).diff(dayjs(start), "day") + 1;
  // Prefer week/month on longer ranges; day only for short windows.
  if (days <= 45) return NewAppsDiscoveryTrendGrain.Day;
  if (days <= 180) return NewAppsDiscoveryTrendGrain.Week;
  return NewAppsDiscoveryTrendGrain.Month;
};

const formatPeriodLabel = (
  dateStr: string,
  grain: NewAppsDiscoveryTrendGrain,
): string => {
  const d = dayjs(dateStr);
  if (grain === NewAppsDiscoveryTrendGrain.Month) {
    return d.format("MMM YYYY");
  }
  if (grain === NewAppsDiscoveryTrendGrain.Week) {
    return d.format("DD MMM");
  }
  return d.format("DD/MM");
};

export const NewAppsDiscoveryTrendChart = ({
  clientId,
  dateRange,
  onSummaryChange,
}: Props) => {
  const navigate = useNavigate();
  const showTenantBreakdown = !clientId;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const dateFrom = dateRange[0]
    ? dayjs(dateRange[0]).format("YYYY-MM-DD")
    : undefined;
  const dateTo = dateRange[1]
    ? dayjs(dateRange[1]).format("YYYY-MM-DD")
    : undefined;

  const grain = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) {
      return NewAppsDiscoveryTrendGrain.Week;
    }
    return resolveGrain(dateRange[0], dateRange[1]);
  }, [dateRange]);

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [dateFrom, dateTo]);

  const trendQuery = useNewAppsDiscoveryTrendQuery(
    {
      dateFrom: dateFrom ?? dayjs().format("YYYY-MM-DD"),
      dateTo: dateTo ?? dayjs().format("YYYY-MM-DD"),
      grain,
      clientId,
    },
    {
      enabled: Boolean(dateFrom && dateTo),
    },
  );

  const chartData = useMemo(() => {
    const points = trendQuery.data?.newAppsDiscoveryTrend?.points ?? [];
    return points.map((point) => ({
      x: formatPeriodLabel(point.date, grain),
      y: point.count,
    }));
  }, [trendQuery.data, grain]);

  const allTenantRows = useMemo((): TenantRow[] => {
    if (!showTenantBreakdown) return [];
    return (
      trendQuery.data?.newAppsDiscoveryTrend?.byTenant?.map((row) => ({
        clientId: row.clientId,
        clientName: row.clientName,
        count: row.count,
      })) ?? []
    );
  }, [trendQuery.data, showTenantBreakdown]);

  const sortedRows = useMemo(() => {
    if (sorting.length === 0) return allTenantRows;
    const [{ id, desc }] = sorting;
    return [...allTenantRows].sort((a, b) => {
      const aVal = a[id as keyof TenantRow];
      const bVal = b[id as keyof TenantRow];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return desc ? bVal - aVal : aVal - bVal;
      }
      return desc
        ? String(bVal).localeCompare(String(aVal))
        : String(aVal).localeCompare(String(bVal));
    });
  }, [allTenantRows, sorting]);

  const pageRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return sortedRows.slice(start, start + pagination.pageSize);
  }, [sortedRows, pagination]);

  const handleTenantClick = useCallback(
    (tenantClientId: string) => {
      navigate(
        routes.portal.clients.client.applications.$buildPath({
          params: { clientId: tenantClientId },
        }),
        { viewTransition: true },
      );
    },
    [navigate],
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("clientName", {
        id: "clientName",
        header: "Tenant",
        cell: (info) => (
          <Clickable
            onClick={() => handleTenantClick(info.row.original.clientId)}
          >
            <Text size={14}>{info.getValue()}</Text>
          </Clickable>
        ),
      }),
      columnHelper.accessor("count", {
        id: "count",
        header: "New apps",
        cell: (info) => <Text size={14}>{info.getValue()}</Text>,
      }),
    ] as ColumnDef<TenantRow>[];
  }, [handleTenantClick]);

  const totalCount = trendQuery.data?.newAppsDiscoveryTrend?.totalCount ?? 0;
  const isLoading = trendQuery.isLoading;
  const isFetching = trendQuery.isFetching;

  useEffect(() => {
    onSummaryChange?.({ totalCount, isLoading });
  }, [isLoading, onSummaryChange, totalCount]);

  return (
    <div className={classes.container}>
      <DashboardChartFrame>
        {dateFrom && dateTo ? (
          <LineChart
            data={[
              {
                id: "new-apps",
                color: "primary",
                data: chartData,
              },
            ]}
            xAxisName="Period"
            yAxisName="New apps"
            tooltipLabel="New apps"
          />
        ) : (
          <Box align="center" justify="center" grow={1} direction="column">
            <Text size={14} color="textSecondary">
              Select a date range to see discovery trend
            </Text>
          </Box>
        )}
      </DashboardChartFrame>

      {showTenantBreakdown ? (
        <div className={classes.breakdown}>
          <Text size={14} weight={600}>
            Contribution by tenant
          </Text>
          <Table
            columns={columns}
            data={pageRows}
            rowCount={sortedRows.length}
            pagination={pagination}
            onPaginationChange={setPagination}
            isLoading={isLoading}
            isFetching={isFetching}
            emptyPlaceholder="No new applications in this range"
            onPageSizeChange={(pageSize) =>
              setPagination({ pageIndex: 0, pageSize })
            }
            sorting={sorting}
            onSortingChange={setSorting}
            showPageSizeDropdown={false}
            showFooter={sortedRows.length > PAGE_SIZE}
            manualSorting={false}
          />
        </div>
      ) : null}
    </div>
  );
};
